import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import PersonaBadge from '../../common/PersonaBadge';

interface Props {
  workflowId: string;
  analytics: any;
}

const PerformanceMatrix: React.FC<Props> = ({ workflowId, analytics }) => {
  const { darkMode } = useTheme();
  const [viewMode, setViewMode] = useState<'heatmap' | 'bar'>('heatmap');

  const executions = analytics?.executions || [];
  const personas = new Set<string>();
  const phases = new Set<string>();

  // Build matrix data from executions
  const matrix: Record<string, Record<string, { total: number; success: number }>> = {};

  executions.forEach((ex: any) => {
    (ex.phases || []).forEach((phase: any) => {
      const phaseLabel = phase.label || phase.id;
      phases.add(phaseLabel);
      (phase.config?.personas || []).forEach((pr: any) => {
        const pName = pr.persona_name || pr.persona_id;
        personas.add(pName);
        if (!matrix[pName]) matrix[pName] = {};
        if (!matrix[pName][phaseLabel]) matrix[pName][phaseLabel] = { total: 0, success: 0 };
        matrix[pName][phaseLabel].total++;
        if (phase.status === 'completed') matrix[pName][phaseLabel].success++;
      });
    });
  });

  const personaList = Array.from(personas);
  const phaseList = Array.from(phases);

  if (personaList.length === 0 || phaseList.length === 0) {
    return (
      <div className={`p-8 text-center rounded-xl border ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
        Performance matrix will populate as workflow executions with persona tracking accumulate.
        Run workflows with assigned personas to see the heatmap.
      </div>
    );
  }

  const getCellColor = (rate: number): string => {
    if (rate >= 80) return darkMode ? 'bg-green-600/40' : 'bg-green-100';
    if (rate >= 50) return darkMode ? 'bg-yellow-600/30' : 'bg-yellow-100';
    return darkMode ? 'bg-red-600/30' : 'bg-red-100';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Personas x Phases
        </h3>
        <div className="flex gap-1">
          {(['heatmap', 'bar'] as const).map(v => (
            <button
              key={v}
              onClick={() => setViewMode(v)}
              className={`px-2 py-1 text-xs rounded ${
                viewMode === v ? 'bg-purple-600 text-white' : darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {viewMode === 'heatmap' ? (
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead>
              <tr>
                <th className={`px-3 py-2 text-left ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Persona</th>
                {phaseList.map(phase => (
                  <th key={phase} className={`px-3 py-2 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {phase.length > 12 ? phase.slice(0, 12) + '..' : phase}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {personaList.map(persona => (
                <tr key={persona}>
                  <td className="px-3 py-2">
                    <PersonaBadge personaId="" personaName={persona} size="xs" clickable={false} />
                  </td>
                  {phaseList.map(phase => {
                    const cell = matrix[persona]?.[phase];
                    const rate = cell ? Math.round((cell.success / cell.total) * 100) : -1;
                    return (
                      <td key={phase} className={`px-3 py-2 text-center ${rate >= 0 ? getCellColor(rate) : ''} rounded`}>
                        {rate >= 0 ? `${rate}%` : '\u2014'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-3">
          {personaList.map(persona => (
            <div key={persona}>
              <div className="flex items-center gap-2 mb-1">
                <PersonaBadge personaId="" personaName={persona} size="xs" clickable={false} />
              </div>
              <div className="flex gap-1">
                {phaseList.map(phase => {
                  const cell = matrix[persona]?.[phase];
                  const rate = cell ? Math.round((cell.success / cell.total) * 100) : 0;
                  return (
                    <div key={phase} className="flex-1">
                      <div className={`h-6 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                        <div
                          className={`h-6 rounded transition-all ${rate >= 80 ? 'bg-green-500' : rate >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                          style={{ width: `${rate}%` }}
                        />
                      </div>
                      <p className={`text-[9px] text-center mt-0.5 truncate ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{phase}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PerformanceMatrix;
